import { createClient } from "@supabase/supabase-js";
import { LOCAL_SUPABASE_URL } from "@/config/local-supabase-ports";

/**
 * Supabase client configuration
 *
 * Environment variables:
 * - SUPABASE_URL: Your Supabase project URL
 * - SUPABASE_ANON_KEY: Your Supabase anonymous/public key
 *
 * For local development with Supabase CLI:
 *   1. Run `bun run db:start` to start local Supabase
 *   2. Ports are project-specific (computed from folder name during setup)
 *   3. Access Studio and Inbucket via the DevToolbar links in the navbar
 *   4. Local credentials are auto-detected when running on localhost
 *
 * For production:
 *   - Run `bun run setup:db` to create a Supabase project
 *   - Or manually set SUPABASE_URL and SUPABASE_ANON_KEY in your hosting environment
 */
const LOCAL_SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0";

// Detect if we're in local development (browser on localhost)
const isLocalDev =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

// Get environment variables
// These placeholders are replaced at build time via Bun.build's define option (see build.ts)
// Using global declarations to avoid issues with process.env replacement
declare const __SUPABASE_URL__: string;
declare const __SUPABASE_ANON_KEY__: string;

// Safely access process.env (only exists on server/build time, not in browser)
const getEnvVar = (key: string): string => {
  try {
    // This will be replaced at build time, but may error in browser during dev
    if (typeof process !== "undefined" && process.env) {
      return (process.env as Record<string, string | undefined>)[key] || "";
    }
  } catch {
    // process is not defined in browser
  }
  return "";
};

// Priority: local dev → build-time injection → env var fallback
const supabaseUrl = isLocalDev
  ? LOCAL_SUPABASE_URL
  : typeof __SUPABASE_URL__ !== "undefined" && __SUPABASE_URL__
    ? __SUPABASE_URL__
    : getEnvVar("SUPABASE_URL");
const supabaseAnonKey = isLocalDev
  ? LOCAL_SUPABASE_ANON_KEY
  : typeof __SUPABASE_ANON_KEY__ !== "undefined" && __SUPABASE_ANON_KEY__
    ? __SUPABASE_ANON_KEY__
    : getEnvVar("SUPABASE_ANON_KEY");

// Placeholder URL for when credentials aren't configured
// This prevents the app from crashing but auth features won't work
const PLACEHOLDER_URL = "https://placeholder.supabase.co";
const PLACEHOLDER_KEY = "placeholder-key";

const finalUrl = supabaseUrl || PLACEHOLDER_URL;
const finalKey = supabaseAnonKey || PLACEHOLDER_KEY;

// Log warning in browser if not configured
const isConfigured = supabaseUrl && supabaseAnonKey;
if (typeof window !== "undefined" && !isConfigured) {
  console.warn(
    "[Auth] Supabase not configured. Run 'bun run setup:db' and restart the dev server to enable authentication.",
  );
}

export const supabase = createClient(finalUrl, finalKey, {
  auth: {
    // Persist session to localStorage by default
    persistSession: true,
    // Auto-refresh the session before it expires
    autoRefreshToken: true,
    // Detect session from URL (for OAuth redirects)
    detectSessionInUrl: true,
  },
});

/**
 * Whether Supabase is properly configured with credentials.
 * If false, auth features will not work.
 */
export const isSupabaseConfigured = isConfigured;

export default supabase;
